import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PageHeader, StatTiles } from '@/components/PageHeader'
import { fetchContracts, fetchTickets } from '@/features/support/api'
import type { ApiError } from '@/lib/api/client'
import type { Contract, Ticket } from '@/types/support'
import { Icon } from '@/components/Icon'
import { ContractDialog } from '@/features/support/ContractDialog'
import { NewTicketDialog } from '@/features/support/NewTicketDialog'
import { TicketPanel } from '@/features/support/TicketPanel'

type Filter = 'all' | 'open' | 'breaching'

/**
 * The support desk.
 *
 * The column that matters is the clock. It is counted in working minutes on the
 * Sunday–Thursday week with Omani public holidays removed (ADR-0009), which is
 * why a ticket raised on Thursday afternoon is not late on Friday morning — and
 * why the figure is one a client can be shown.
 */
export function SupportPage() {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<Filter>('open')
  const [creating, setCreating] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [contractOpen, setContractOpen] = useState<Contract | 'new' | null>(null)

  const tickets = useQuery<Ticket[], ApiError>({
    queryKey: ['tickets', filter],
    queryFn: () =>
      fetchTickets(
        filter === 'all' ? {} : filter === 'open' ? { open: true } : { breaching: true },
      ),
    retry: false,
  })

  const contracts = useQuery<Contract[], ApiError>({
    queryKey: ['contracts'],
    queryFn: fetchContracts,
    retry: false,
  })

  if (tickets.isPending) {
    return <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
  }

  const rows = tickets.data ?? []

  // Every clock on this screen is judged against the moment the data was
  // fetched, not against the moment each row happens to render. One reference
  // point means two rows an inch apart cannot disagree about what "now" is —
  // and it keeps the render pure, which reading the wall clock would not.
  const asOf = tickets.dataUpdatedAt
  const breaching = rows.filter((ticket) => isOverdue(ticket, asOf)).length

  const FILTERS: { id: Filter; label: string }[] = [
    { id: 'open', label: t('support.openOnly') },
    { id: 'breaching', label: t('support.breaching') },
    { id: 'all', label: t('support.all') },
  ]

  return (
    <div>
      <PageHeader
        title={t('support.title')}
        subtitle={t('support.subtitle')}
        action={
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="bg-brand-600 flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-medium text-white"
          >
            <Icon name="plus" className="size-3.5" />
            {t('support.newTicket')}
          </button>
        }
      />

      <StatTiles
        items={[
          { label: t('support.title'), value: rows.length },
          { label: t('support.breaching'), value: breaching },
          { label: t('support.contracts'), value: contracts.data?.length ?? 0 },
        ]}
      />

      <p className="text-ink-700 mb-4 max-w-3xl text-sm">{t('support.slaExplainer')}</p>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {FILTERS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setFilter(option.id)}
            aria-pressed={filter === option.id}
            className={`rounded border px-3 py-1 text-sm ${
              filter === option.id
                ? 'border-brand-600 bg-brand-600 text-white'
                : 'border-line bg-surface text-ink-700 hover:bg-surface-2'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
          {t('support.noTickets')}
        </p>
      ) : (
        <div className="border-line bg-surface mb-9 overflow-x-auto rounded-md border">
          <table className="w-full min-w-[840px] text-sm">
            <thead>
              <tr className="border-line-strong text-ink-500 border-b font-mono text-[10px] tracking-widest uppercase">
                <th className="px-4 py-2.5 text-start font-medium">{t('support.ticket')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('support.contract')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('support.status')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('support.channel')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('support.firstResponse')}</th>
                <th className="px-4 py-2.5 text-start font-medium">{t('support.resolution')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((ticket) => (
                <tr key={ticket.id} className="border-line hover:bg-surface-2 border-b last:border-0">
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={() => setOpen(ticket.key)}
                      className="text-start"
                    >
                      <span className="text-brand-600 font-mono text-xs">{ticket.key}</span>
                      <div className="text-ink-900 mt-0.5">{ticket.title}</div>
                    </button>
                  </td>
                  <td className="text-ink-700 px-4 py-2.5">{ticket.contract?.name ?? '—'}</td>
                  <td className="text-ink-700 px-4 py-2.5">{ticket.status?.name ?? '—'}</td>
                  <td className="text-ink-500 px-4 py-2.5 font-mono text-xs">
                    {ticket.channel ?? '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <Clock
                      dueAt={ticket.sla?.firstResponseDueAt ?? null}
                      metAt={ticket.sla?.firstRespondedAt ?? null}
                      paused={ticket.sla?.paused ?? false}
                      metLabel={t('support.responded')}
                      asOf={asOf}
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <Clock
                      dueAt={ticket.sla?.resolutionDueAt ?? null}
                      metAt={ticket.sla?.resolvedAt ?? null}
                      paused={ticket.sla?.paused ?? false}
                      metLabel={t('support.resolved')}
                      asOf={asOf}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section>
        <div className="mb-3 flex items-center gap-2">
          <h2 className="text-ink-900 text-sm font-semibold">{t('support.contracts')}</h2>
          <button
            type="button"
            onClick={() => setContractOpen('new')}
            className="text-brand-600 ms-auto flex items-center gap-1 text-[12px]"
          >
            <Icon name="plus" className="size-3" />
            {t('support.newContract')}
          </button>
        </div>
        {(contracts.data ?? []).length === 0 ? (
          <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
            {t('support.noContracts')}
          </p>
        ) : (
          <ul className="border-line bg-surface divide-line divide-y rounded-md border">
            {(contracts.data ?? []).map((contract) => (
              <li key={contract.id} className="hover:bg-surface-2 px-4 py-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <button
                    type="button"
                    onClick={() => setContractOpen(contract)}
                    className="text-ink-900 text-start font-medium"
                  >
                    {contract.name}
                  </button>
                  <span className="text-ink-500 text-sm">{contract.client?.name ?? '—'}</span>
                  <span className="text-ink-500 ms-auto font-mono text-xs">
                    {contract.startsOn} → {contract.endsOn ?? '—'}
                  </span>
                </div>
                {contract.slaPolicies.length === 0 ? null : (
                  <ul className="text-ink-700 mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    {contract.slaPolicies.map((policy) => (
                      <li key={policy.id} className="font-mono">
                        {policy.name}: {policy.firstResponseMinutes}
                        {t('support.minutes')} / {policy.resolutionMinutes}
                        {t('support.minutes')}
                        <span className="text-ink-500">
                          {' '}
                          ·{' '}
                          {policy.businessHoursOnly
                            ? t('support.workingHoursOnly')
                            : t('support.aroundTheClock')}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      {creating ? <NewTicketDialog onClose={() => setCreating(false)} /> : null}
      {open === null ? null : (
        <TicketPanel key={open} ticketKey={open} asOf={asOf} onClose={() => setOpen(null)} />
      )}
      {contractOpen === null ? null : (
        <ContractDialog
          key={contractOpen === 'new' ? 'new' : contractOpen.id}
          {...(contractOpen === 'new' ? {} : { contract: contractOpen })}
          onClose={() => setContractOpen(null)}
        />
      )}
    </div>
  )
}

function isOverdue(ticket: Ticket, asOf: number): boolean {
  const sla = ticket.sla
  if (sla === null || sla.paused || asOf === 0) return false

  const missed = (dueAt: string | null, metAt: string | null): boolean =>
    dueAt !== null && metAt === null && Date.parse(dueAt) < asOf

  return missed(sla.firstResponseDueAt, sla.firstRespondedAt) || missed(sla.resolutionDueAt, sla.resolvedAt)
}

function Clock({
  dueAt,
  metAt,
  paused,
  metLabel,
  asOf,
}: {
  dueAt: string | null
  metAt: string | null
  paused: boolean
  metLabel: string
  asOf: number
}) {
  const { t } = useTranslation()

  if (dueAt === null) return <span className="text-ink-500">—</span>

  if (metAt !== null) {
    return (
      <span className="font-mono text-xs text-[color:var(--color-state-done)]">
        {metLabel} {metAt.slice(0, 10)}
      </span>
    )
  }

  if (paused) {
    return <span className="text-ink-500 font-mono text-xs">{t('support.paused')}</span>
  }

  const late = asOf !== 0 && Date.parse(dueAt) < asOf

  return (
    <span
      className={`font-mono text-xs ${
        late ? 'font-semibold text-[color:var(--color-state-blocked)]' : 'text-ink-700'
      }`}
    >
      {late ? t('support.overdue') : t('support.due')} {dueAt.slice(0, 10)}
    </span>
  )
}
